import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { fetchSession, login as apiLogin, logout as apiLogout, bootstrap as apiBootstrap, type AuthSession, type SessionUser } from "./auth-api";
import { setUnauthorizedHandler } from "./api";

const AuthContext = createContext<{
  ready: boolean;
  session: AuthSession;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  bootstrap: (input: { name: string; email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
} | null>(null);

const EMPTY: AuthSession = {
  configured: false,
  has_users: false,
  email: false,
  email_from: "newsletter@altibbi.me",
  me: null,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession>(EMPTY);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    const next = await fetchSession();
    setSession(next);
    setReady(true);
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // A 401 from any data route means the session lapsed; re-check it so
  // RequireAuth sends the user to /login rather than rendering stale screens.
  useEffect(() => {
    setUnauthorizedHandler(() => { void refresh(); });
    return () => setUnauthorizedHandler(null);
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const me: SessionUser = await apiLogin(email, password);
    setSession((cur) => ({ ...cur, has_users: true, me }));
  }, []);

  const bootstrap = useCallback(async (input: { name: string; email: string; password: string }) => {
    const me = await apiBootstrap(input);
    setSession((cur) => ({ ...cur, has_users: true, me, configured: true }));
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setSession((cur) => ({ ...cur, me: null }));
  }, []);

  const value = useMemo(() => ({ ready, session, refresh, login, bootstrap, logout }), [ready, session, refresh, login, bootstrap, logout]);
  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
